import invariant from 'tiny-invariant'
import {
  MeetingHandling,
  MeetingHandlingDiscriminator,
  ProductFeatureWithRoundRobin,
} from '@chilipiper/api-client/src/distribution'
import {
  PublicRoutingInfoResponse,
  SanitizedAssigneeSelection,
  ScheduleMeetingRequestPublic,
  SelectRouteResponse,
} from '@chilipiper/api-client/src/handoff'
import { PickedMeetingLocation } from '@chilipiper/api-client/src/chat-platform-chat'
import * as v from 'valibot'
import {
  AdminIndividualSchedulingLink,
  GroupSchedulingLink1,
  HandoffRouting,
  MeetingAttempt,
  MeetingAttemptResponse,
  MeetingAttendees,
  MeetingLimitMeetingLimitSource,
  OwnershipSchedulingLink,
  PersonalIndividualSchedulingLink,
  ProductFeature,
  RoundRobinSchedulingLink,
} from '@chilipiper/api-client/src/user-availability'
import {
  GuestFormDataFilledDataFields,
  GuestFormDataFilledMixed,
  MeetingAttendees as MeetingAttendeesRequest,
  ScheduleMeetingRequestV2FeatureAdminSchedulingLink,
  ScheduleMeetingRequestV2FeatureRoundRobinSchedulingLink,
  ScheduleMeetingRequestV2FeaturePersonalSchedulingLink,
  ScheduleMeetingRequestV2FeatureGroupSchedulingLink,
  ScheduleMeetingRequestV2FeatureOwnershipSchedulingLink,
  GroupSchedulingLink,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  GuestFormDataFields,
  GuestFormMixed,
  MapLocaleGuestFormLocalization,
  MapString,
  MeetingLocation,
  PublicMeetingType,
  QuestionDataType,
} from '@chilipiper/api-client/src/meeting-types'
import { DataFieldDataType } from '@chilipiper/api-client/src/chili-crm'
import { OrderedListOfUsers } from '@chilipiper/distribution-components'
import { getAdditionalRequiredAttendeesId } from '@chilipiper/booking-flow'
import * as R from 'remeda'
import { AssigneeOptions } from './types'

type GuestFormData = GuestFormDataFilledDataFields | GuestFormDataFilledMixed

/**
 * Transforms a MeetingLocation to a PickedMeetingLocation with all required fields. This ensures
 * that location types requiring a host field (like ZoomOneTimeLink) are properly handled.
 */
export const transformMeetingLocation = (location: MeetingLocation): PickedMeetingLocation => {
  if (
    location.type === 'ZoomOneTimeLink' ||
    location.type === 'WebexLink' ||
    location.type === 'GongLink' ||
    location.type === 'GoToMeetingLink' ||
    location.type === 'HostsDefaultConferenceDetails' ||
    location.type === 'HostsDefaultPhysicalLocation' ||
    location.type === 'TeamsMeetingLink' ||
    location.type === 'RingCentralLink'
  ) {
    return {
      type: location.type,
      host: location.host,
    }
  }

  if (location.type === 'DefinedInMeetingType') {
    return {
      type: location.type,
      value: location.value,
    }
  }

  return {
    type: location.type,
  }
}

export const getAssigneeOptions = (
  meetingAttempt?: MeetingAttempt,
  handoffRouting?: PublicRoutingInfoResponse,
  distribution?: OrderedListOfUsers,
  distributionId?: string,
  isRescheduling?: boolean
): AssigneeOptions => {
  if (
    meetingAttempt?.productFeature.type === 'AdminIndividualSchedulingLink' ||
    meetingAttempt?.productFeature.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      combinedAvailability: true,
      hostId: meetingAttempt.productFeature.hostId,
      isReservationFlow: true,
    }
  }

  if (meetingAttempt?.productFeature.type === 'HandoffRouting' && handoffRouting) {
    if (handoffRouting.type !== 'PublicRoutingInfoForSelected') {
      return
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'User') {
      return {
        combinedAvailability: true,
        hostId: handoffRouting.selectResponse.selectedAssignee.id,
        isReservationFlow: true,
      }
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'UserFromDistribution') {
      return {
        combinedAvailability: true,
        hostId: handoffRouting.selectResponse.selectedAssignee.user,
        isReservationFlow: true,
      }
    }

    if (handoffRouting.selectResponse.selectedAssignee.type === 'FromDistribution') {
      return getRoundRobinAssigneesInfo(
        handoffRouting.selectResponse.selectedAssignee.algorithm as MeetingHandlingDiscriminator,
        distributionId,
        handoffRouting.selectResponse.selectedAssignee.users,
        handoffRouting.selectResponse.selectedAssignee.vacations
      )
    }

    throw new Error('invalid handoff routing select response')
  }

  if (meetingAttempt?.productFeature.type === 'RoundRobinSchedulingLink' && distribution) {
    return getRoundRobinAssigneesInfo(
      (() => {
        if (isRescheduling) {
          if (distribution.reassignmentType === 'AnyTeamMember') {
            return 'Flexible'
          }
          return 'Strict'
        }
        return distribution.roundRobinKind
      })(),
      distributionId,
      distribution.users,
      distribution.vacations
    )
  }
}

const getRoundRobinAssigneesInfo = (
  roundRobinKind: MeetingHandlingDiscriminator,
  distributionId?: string,
  allUsers?: string[],
  vacations?: string[]
): AssigneeOptions => {
  if (!allUsers) {
    return {
      combinedAvailability: false,
      isReservationFlow: false,
      distributionId: distributionId ?? '',
      assigneeIds: [],
    }
  }

  if (roundRobinKind === 'Flexible') {
    return {
      combinedAvailability: false,
      isReservationFlow: false,
      distributionId: distributionId ?? '',
      assigneeIds: getFlexibleRoundRobinAssigneesList(allUsers, vacations),
    }
  }

  const filteredOutVacationUsers = allUsers?.filter(user => !vacations?.includes(user)) ?? []
  if (filteredOutVacationUsers[0]) {
    return {
      combinedAvailability: true,
      isReservationFlow: true,
      hostId: filteredOutVacationUsers[0],
    }
  }
  // If it's RR and all users are OFF
  // we display availability of all distribution users
  return {
    combinedAvailability: false,
    isReservationFlow: false,
    distributionId: distributionId ?? '',
    assigneeIds: allUsers || [],
  }
}

const getFlexibleRoundRobinAssigneesList = (
  allUsers?: string[],
  vacations?: string[]
): string[] => {
  const filteredOutVacationUsers = allUsers?.filter(user => !vacations?.includes(user)) ?? []
  if (filteredOutVacationUsers.length > 0) {
    return filteredOutVacationUsers
  }
  // If it's RR and all users are OFF
  // we display availability of all distribution users
  return allUsers || []
}
export const mapQuestionDataTypeToDataFieldDataType = (
  questionDataType: QuestionDataType
): DataFieldDataType => {
  switch (questionDataType.type) {
    case 'QuestionCheckboxes':
      return {
        type: 'Checkbox',
        values: questionDataType.values,
      }
    case 'QuestionEmail':
      return {
        type: 'Email',
      }

    case 'QuestionMultiLineText':
      return { type: 'LongText' }
    case 'QuestionPhone':
      return {
        type: 'Phone',
      }
    case 'QuestionPicklist':
      return { type: 'Picklist', values: questionDataType.values }
    case 'QuestionRadioButtons':
      return { type: 'RadioButton', values: questionDataType.values }
    case 'QuestionSingleLineText':
      return { type: 'ShortText' }
    case 'QuestionTrueFalse':
      return { type: 'TrueFalse' }
  }
}
export const getHandoffWithReservationPayload = ({
  reservationId,
  assigneeId,
  timezone,
  location,
}: {
  assigneeId?: string
  location?: MeetingLocation
  reservationId: string
  timezone?: string
}) => {
  invariant(location, 'Location not found')
  invariant(assigneeId, 'Assignee not found')
  invariant(location.type !== 'GoToMeetingLink', 'GoToMeetingLink is not supported here yet')

  const payload: ScheduleMeetingRequestPublic = {
    type: 'ScheduleByReservation',
    assigneeId,
    reservationId,
    guestTimezone: timezone,
    location,
    secondaryFeature: ['SuggestedTimes'],
  }

  return payload
}
export const getHandoffStrictAssigneeId = (selectedAssignee?: SanitizedAssigneeSelection) => {
  if (selectedAssignee?.type === 'User') {
    return selectedAssignee.id
  }

  if (selectedAssignee?.type === 'UserFromDistribution') {
    return selectedAssignee.user
  }
}
export const getGuestEmail = (
  productFeature?: ProductFeature,
  handoffRouting?: PublicRoutingInfoResponse
) => {
  if (!productFeature) {
    return
  }
  if (
    productFeature.type === 'AdminIndividualSchedulingLink' ||
    productFeature.type === 'PersonalIndividualSchedulingLink' ||
    productFeature.type === 'RoundRobinSchedulingLink'
  ) {
    return productFeature.primaryGuestV2?.email
  }
  if (
    productFeature.type === 'HandoffRouting' &&
    handoffRouting?.type === 'PublicRoutingInfoForSelected'
  ) {
    return handoffRouting.primaryGuest.email
  }
  return undefined
}

/**
 * Extracts the assignee ID for a selected time slot from availability data. Supports both userIds
 * format and startTimes format with fallback to closest slot.
 */
export const getAssigneeForSelectedTimeSlot = (
  selectedTime: string,
  timeSlots?:
    | { userIds?: string[] }
    | { startTimes?: Array<{ startTime: string; availableAssignees?: string[] }> }
): string | undefined => {
  if (!timeSlots) {
    return undefined
  }

  // Handle userIds format (simple assignee list)
  if ('userIds' in timeSlots) {
    return timeSlots.userIds?.[0]
  }

  // Handle startTimes format (time-specific assignees)
  if ('startTimes' in timeSlots && timeSlots.startTimes) {
    const normalizeTime = (time: string): string => {
      return new Date(time).toISOString().replace(/\.\d{3}Z$/, 'Z')
    }

    const normalizedSelectedTime = normalizeTime(selectedTime)

    // First attempt: exact match
    const exactMatch = timeSlots.startTimes.find(
      slot => normalizeTime(slot.startTime) === normalizedSelectedTime
    )

    if (exactMatch) {
      return exactMatch.availableAssignees?.[0]
    }

    // TODO: Remove this fallback logic once we migrate to booking app

    // Fallback: find closest slot within 30 minutes on same day
    const selectedDate = new Date(selectedTime)
    let closestSlot: (typeof timeSlots.startTimes)[0] | null = null
    let closestDiff = Infinity

    for (const timeSlot of timeSlots.startTimes) {
      const slotDate = new Date(timeSlot.startTime)
      const timeDiff = Math.abs(selectedDate.getTime() - slotDate.getTime())
      const timeDiffMinutes = timeDiff / (1000 * 60)

      const isSameDay = selectedDate.toDateString() === slotDate.toDateString()
      const isWithinThreshold = timeDiffMinutes <= 30
      const isCloser = timeDiff < closestDiff

      if (isSameDay && isWithinThreshold && isCloser) {
        closestDiff = timeDiff
        closestSlot = timeSlot
      }
    }

    return closestSlot?.availableAssignees?.[0]
  }

  return undefined
}

export const getMeetingRequestFeature = ({
  productFeature,
  reservationId,
  distribution,
  distributionId,
  assigneesIds,
  selectedTimeAssigneeId,
}: Omit<FeaturePayload, 'duration' | 'slotStartAt'> & { selectedTimeAssigneeId?: string }):
  | RequestFeature
  | undefined => {
  if (productFeature.type === 'AdminIndividualSchedulingLink' && reservationId) {
    const adminSchedulingLinkFeature: FeatureAdminSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      linkId: productFeature.linkId,
      type: 'AdminSchedulingLink',
    }

    return adminSchedulingLinkFeature
  }

  if (productFeature.type === 'PersonalIndividualSchedulingLink' && reservationId) {
    const personalSchedulingLinkFeature: FeaturePersonalSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      meetingTypeId: productFeature.meetingTypeId,
      type: 'PersonalSchedulingLink',
    }

    return personalSchedulingLinkFeature
  }

  if (productFeature.type === 'RoundRobinSchedulingLink' && reservationId) {
    // Prioritize specific assignee from selected time slot, then fallback to distribution
    const assigneeId = selectedTimeAssigneeId ?? assigneesIds?.[0] ?? distribution?.users?.[0] ?? ''

    if (!assigneeId) {
      return undefined
    }

    const roundRobinFeature: FeatureRoundRobinSchedulingLinkRequest = {
      timeAndAssignment: {
        reservationId,
        type: 'WithReservation',
        assignments: distributionId
          ? [
              {
                type: 'AssignedViaDistribution',
                assigneeId,
                distributionId,
              },
            ]
          : [
              {
                type: 'ManuallyAssigned',
                assigneeId,
              },
            ],
        hostId: assigneeId,
      },
      linkId: productFeature.linkId,
      type: 'RoundRobinSchedulingLink',
    }

    return roundRobinFeature
  }

  if (productFeature.type === 'GroupSchedulingLink' && reservationId && productFeature.hostId) {
    const groupSchedulingLinkFeature: FeatureGroupSchedulingLinkRequest = {
      timeAndAssignment: {
        reservationId,
        type: 'WithReservation',
        hostId: productFeature.hostId,
      },
      linkId: productFeature.linkId,
      type: 'GroupSchedulingLink',
    }

    return groupSchedulingLinkFeature
  }

  if (productFeature.type === 'OwnershipSchedulingLink' && reservationId && distributionId) {
    const ownershipSchedulingLinkFeature: FeatureOwnershipSchedulingLinkRequest = {
      timeAndAssignment: {
        reservationId,
        type: 'WithReservation',
        assignments: productFeature.assignment?.assigneeId
          ? [
              {
                type: 'AssignedViaDistribution',
                assigneeId: productFeature.assignment.assigneeId,
                distributionId,
              },
            ]
          : [],
        hostId: productFeature.assignment?.assigneeId ?? '',
      },
      linkId: productFeature.linkId,
      type: 'OwnershipSchedulingLink',
    }

    return ownershipSchedulingLinkFeature
  }
}

export const getSuggestedTimeRouting = (routing?: PublicRoutingInfoResponse) => {
  if (
    routing?.type === 'PublicRoutingInfoForSelected' &&
    routing.selectResponse.type === 'SuggestedTimesCreated'
  ) {
    return routing.selectResponse
  }

  return
}

const getUniqUserIdsList = (userIds: string[]) => R.unique(userIds)

export const getMeetingAttemptReservationPayload = ({
  meetingAttempt,
  slotStartsAt,
  routing,
  meetingTypeRef,
  availableAssignees,
  algorithm,
  groupSchedulingLink,
  meetingLimit = {
    type: 'NoMeetingLimit',
  },
  distributionId,
}: {
  meetingAttempt?: MeetingAttemptResponse
  meetingTypeRef: {
    id: string
    timestamp: string
  }
  routing?: SelectRouteResponse
  slotStartsAt: string
  distributionId?: string
  algorithm?: MeetingHandling['type']
  availableAssignees?: string[]
  groupSchedulingLink?: GroupSchedulingLink
  meetingLimit?: MeetingLimitMeetingLimitSource
}) => {
  if (meetingAttempt?.type === 'Available') {
    const payloadBase = {
      slotStartsAt,
      meetingTypeRef,
      durationOverride: meetingAttempt.attempt.meetingInvite.meetingDurationOverride,
    }

    const additionalAttendees = getAdditionalRequiredAttendeesId(
      meetingAttempt.attempt.additionalAttendees
    )

    if (
      meetingAttempt.attempt.productFeature.type === 'AdminIndividualSchedulingLink' ||
      meetingAttempt.attempt.productFeature.type === 'PersonalIndividualSchedulingLink'
    ) {
      return {
        ...payloadBase,
        meetingLimit,
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.hostId,
          ...additionalAttendees,
        ]),
      }
    }

    if (meetingAttempt.attempt.productFeature.type === 'HandoffRouting') {
      let userIds: string[] = []

      if (routing?.selectedAssignee.type === 'User') {
        userIds = getUniqUserIdsList([routing.selectedAssignee.id, ...additionalAttendees])
      }

      if (routing?.selectedAssignee.type === 'UserFromDistribution') {
        userIds = [routing.selectedAssignee.user, ...additionalAttendees]
      }

      if (
        routing?.selectedAssignee.type === 'FromDistribution' &&
        routing?.selectedAssignee.algorithm === 'Strict'
      ) {
        userIds = getUniqUserIdsList([
          routing.selectedAssignee.users[0] ?? '',
          ...additionalAttendees,
        ])
      }

      return {
        ...payloadBase,
        meetingLimit,
        userIds,
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      meetingAttempt.attempt.productFeature.assigneeId &&
      distributionId
    ) {
      return {
        ...payloadBase,
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.assigneeId,
          ...additionalAttendees,
        ]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      algorithm === 'Strict' &&
      availableAssignees?.length &&
      distributionId
    ) {
      return {
        ...payloadBase,
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([availableAssignees[0] ?? '', ...additionalAttendees]),
      }
    }

    // More general RoundRobinSchedulingLink case for flexible assignments
    if (
      meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink' &&
      availableAssignees?.length &&
      distributionId
    ) {
      return {
        ...payloadBase,
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([availableAssignees[0] ?? '', ...additionalAttendees]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'OwnershipSchedulingLink' &&
      meetingAttempt.attempt.productFeature.assignment?.assigneeId &&
      distributionId
    ) {
      return {
        ...payloadBase,
        meetingLimit: {
          source: {
            distributionId,
          },
          type: 'FetchMeetingLimitByDistribution' as const,
        },
        userIds: getUniqUserIdsList([
          meetingAttempt.attempt.productFeature.assignment.assigneeId,
          ...additionalAttendees,
        ]),
      }
    }

    if (
      meetingAttempt.attempt.productFeature.type === 'GroupSchedulingLink' &&
      groupSchedulingLink
    ) {
      const hostId = meetingAttempt.attempt.productFeature.hostId ?? groupSchedulingLink.host.userId

      return {
        ...payloadBase,
        meetingLimit,
        userIds: getUniqUserIdsList([hostId, ...additionalAttendees]),
      }
    }
  }

  return
}

type RequestFeature =
  | FeatureAdminSchedulingLinkRequest
  | FeatureRoundRobinSchedulingLinkRequest
  | FeaturePersonalSchedulingLinkRequest
  | FeatureGroupSchedulingLinkRequest
  | FeatureOwnershipSchedulingLinkRequest

type FeaturePayload = {
  productFeature:
    | AdminIndividualSchedulingLink
    | PersonalIndividualSchedulingLink
    | RoundRobinSchedulingLink
    | HandoffRouting
    | GroupSchedulingLink1
    | OwnershipSchedulingLink
  reservationId?: string
  distributionId?: string
  slotStartAt?: string
  duration?: string
  assigneesIds?: string[]
  distribution?: OrderedListOfUsers
}
export const mapMeetingAttendees = (attendees?: MeetingAttendees): MeetingAttendeesRequest => {
  if (!attendees) {
    return {
      type: 'NoAdditionalAttendees',
    }
  }
  return attendees.type === 'SomeAttendees'
    ? {
        ...attendees,
        attendees: attendees.attendees.map(attendee => ({
          ...attendee,
          responseStatus: 'NeedsAction',
        })),
      }
    : attendees
}
export const getRoundRobinSchedulingLinkId = (
  meetingAttempt: MeetingAttemptResponse | undefined
) => {
  if (
    meetingAttempt?.type === 'Available' &&
    meetingAttempt.attempt.productFeature.type === 'RoundRobinSchedulingLink'
  ) {
    return meetingAttempt.attempt.productFeature.linkId
  }
  return undefined
}

// V1 types for use with scheduleAMeetingGuestEndpoint
type FeatureAdminSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestV2FeatureAdminSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeatureRoundRobinSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestV2FeatureRoundRobinSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeaturePersonalSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestV2FeaturePersonalSchedulingLink,
  'timeAndAssignment' | 'meetingTypeId' | 'type'
>

type FeatureGroupSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestV2FeatureGroupSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeatureOwnershipSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestV2FeatureOwnershipSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type'
>

type FeatureAdminSchedulingLinkRequestWithGuestFormData = FeatureAdminSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestV2FeatureAdminSchedulingLink, 'guestFormData'>

type FeatureRoundRobinSchedulingLinkRequestWithGuestFormData =
  FeatureRoundRobinSchedulingLinkRequest &
    Pick<ScheduleMeetingRequestV2FeatureRoundRobinSchedulingLink, 'guestFormData'>

type FeaturePersonalSchedulingLinkRequestWithGuestFormData = FeaturePersonalSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestV2FeaturePersonalSchedulingLink, 'guestFormData'>

type FeatureGroupSchedulingLinkRequestWithGuestFormData = FeatureGroupSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestV2FeatureGroupSchedulingLink, 'guestFormData'>

type FeatureOwnershipSchedulingLinkRequestWithGuestFormData =
  FeatureOwnershipSchedulingLinkRequest &
    Pick<ScheduleMeetingRequestV2FeatureOwnershipSchedulingLink, 'guestFormData'>

type RequestFeatureWithGuestFormData =
  | FeatureAdminSchedulingLinkRequestWithGuestFormData
  | FeatureRoundRobinSchedulingLinkRequestWithGuestFormData
  | FeaturePersonalSchedulingLinkRequestWithGuestFormData
  | FeatureGroupSchedulingLinkRequestWithGuestFormData
  | FeatureOwnershipSchedulingLinkRequestWithGuestFormData

export const getMeetingRequestFeatureWithGuestFormData = (
  feature: RequestFeature,
  formValues: GuestFormValues,
  meetingType: PublicMeetingType
): RequestFeatureWithGuestFormData => {
  if (feature.type === 'PersonalSchedulingLink') {
    return {
      ...feature,
      guestFormData: {
        fieldValues: {},
        questionValues: {},
        ...getGuestFormData<GuestFormData>(formValues, meetingType),
      },
    }
  }

  if (feature.type === 'GroupSchedulingLink') {
    return {
      ...feature,
      guestFormData: getGuestFormData<GuestFormDataFilledDataFields>(formValues, meetingType),
    }
  }

  if (feature.type === 'OwnershipSchedulingLink') {
    return {
      ...feature,
      guestFormData: getGuestFormData<GuestFormDataFilledDataFields>(formValues, meetingType),
    }
  }

  return {
    ...feature,
    guestFormData: getGuestFormData<GuestFormDataFilledDataFields>(formValues, meetingType),
  }
}

const FORM_TYPE: Record<
  GuestFormDataFields['type'] | GuestFormMixed['type'],
  'FilledDataFields' | 'FilledMixed'
> = {
  DataFields: 'FilledDataFields',
  Mixed: 'FilledMixed',
}

export type GuestFormValues = {
  [key: string]: string | string[]
}

const getGuestFormData = <T = GuestFormData>(
  formValues: GuestFormValues,
  meetingType?: PublicMeetingType
): T => {
  const formType = FORM_TYPE[meetingType?.guestForm.type ?? 'Mixed']

  const formDataValues = Object.keys(formValues).reduce((result, key) => {
    const field = formValues[key]
    const value = Array.isArray(field) ? field.join(',') : field

    if (!value) {
      return result
    }

    return {
      ...result,
      [key]: value,
    }
  }, {} as MapString)

  if (meetingType?.guestForm.type !== 'Mixed') {
    return {
      values: formDataValues,
      type: formType,
    } as T
  }

  return Object.keys(formDataValues).reduce(
    (result, key) => {
      if (isMixedGuestForm(meetingType.guestForm)) {
        const field = meetingType?.guestForm.items.find(
          field =>
            (field.type === 'Question' && field.id === key) ||
            (field.type === 'Field' && field.reference === key)
        )

        const typeKey = field?.type === 'Question' ? 'questionValues' : 'fieldValues'

        return {
          ...result,
          [typeKey]: {
            ...result[typeKey],
            [key]: formDataValues[key],
          },
        }
      }

      return result
    },
    {
      fieldValues: {},
      questionValues: {},
      type: formType,
    }
  ) as T
}

const isMixedGuestForm = (
  guestForm?: GuestFormDataFields | GuestFormMixed
): guestForm is GuestFormMixed => guestForm?.type === 'Mixed'

export const ErrorSchema = v.object({
  error: v.object({
    errorCode: v.string(),
    errorMessage: v.string(),
  }),
})

export const getProductFeatureTypeWithRoundRobin = (
  meetingAttempt?: MeetingAttemptResponse
): ProductFeatureWithRoundRobin | undefined => {
  const routingProductFeatureTypes: Record<string, ProductFeatureWithRoundRobin> = {
    ChatPlaybook: 'ChatPlaybook',
    DistroRouter: 'DistroRouter',
    HandoffRouting: 'HandoffRouter',
    ConciergeRouter: 'ConciergeRouter',
    RoundRobinSchedulingLink: 'RoundRobinSchedulingLink',
    OwnershipSchedulingLink: 'OwnershipSchedulingLink',
  }

  if (!meetingAttempt || meetingAttempt.type === 'NotFound') {
    return
  }

  if (meetingAttempt.type === 'Available') {
    const productFeatureType = meetingAttempt.attempt.productFeature.type
    return Object.keys(routingProductFeatureTypes).includes(productFeatureType)
      ? routingProductFeatureTypes[productFeatureType]
      : undefined
  }
}

export const getRoutingIdFromMeetingAttempt = (meetingAttempt?: MeetingAttemptResponse) => {
  if (
    meetingAttempt?.type === 'Available' &&
    meetingAttempt.attempt.productFeature.type === 'HandoffRouting'
  ) {
    return meetingAttempt.attempt.productFeature.routingId
  }
  return
}

/**
 * Extracts the correct meeting type ID from various sources.
 *
 * For RoundRobinSchedulingLink, OwnershipSchedulingLink, GroupSchedulingLink cases, this returns
 * the selectedMeetingTypeId. For other cases, returns the original block meeting type ID.
 *
 * Note: The useMeetingTypeResolution hook handles cases where the backend sends scheduling link IDs
 * instead of meeting type IDs.
 *
 * @param blockMeetingType - The meeting type ID from the block payload
 * @param meetingAttempt - Optional meeting attempt data with product feature info
 * @returns The resolved meeting type ID
 */
export const getMeetingTypeId = (
  blockMeetingType: string,
  meetingAttempt?: MeetingAttemptResponse
) => {
  if (
    (meetingAttempt?.type === 'Available' || meetingAttempt?.type === 'Booked') &&
    (meetingAttempt?.attempt.productFeature.type === 'RoundRobinSchedulingLink' ||
      meetingAttempt?.attempt.productFeature.type === 'OwnershipSchedulingLink' ||
      meetingAttempt?.attempt.productFeature.type === 'GroupSchedulingLink')
  ) {
    const selectedMeetingTypeId = meetingAttempt.attempt.productFeature.selectedMeetingTypeId
    if (selectedMeetingTypeId) {
      return selectedMeetingTypeId
    }
  }

  return blockMeetingType
}

export const getFieldText = (fieldLocalization: MapLocaleGuestFormLocalization) => {
  if (fieldLocalization['en-US']) {
    return fieldLocalization['en-US']
  }
  return (
    Object.entries(fieldLocalization).find(([key, value]) => {
      if (key.startsWith('en')) {
        return value
      }
    })?.[1] ?? Object.values(fieldLocalization)[0]
  )
}
